import { useAtom, useAtomValue } from 'jotai'
import { Maximize2 } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { SuccessStep } from '@/src/components/trade-steps/success-step'
import { isCompleteAtom, routeAtom } from '@/src/stores/route-machine/atoms'
import {
  activeStepsAtom,
  isCurrentStepModeAtom,
  stepsNeededAtom,
  TradeStep as TradeStepType,
  viewModeAtom,
} from '@/src/stores/trade/atoms'
import { cn } from '@/src/utils/styles'
import { StepsOverview } from '../route-details'
import { GasStationStep } from './gas-station-step'
import { RecoveryStep, RecoverySuccessStep } from './recovery-step'
import { StartTradeStep } from './start-trade-step'
import { SummaryStep } from './summary-step'
import { TradeStep } from './trade-step'

interface TradeStepsProps {
  className?: string
}

export const TradeSteps = ({ className }: TradeStepsProps) => {
  const isCurrentStepMode = useAtomValue(isCurrentStepModeAtom)
  const [, setMode] = useAtom(viewModeAtom)

  useEffect(() => {
    return () => {
      setMode('current-step')
    }
  }, [setMode])

  return (
    <div className='w-full'>
      {!isCurrentStepMode && <StepsOverview />}
      {isCurrentStepMode && <CurrentStep className={className} />}
    </div>
  )
}

interface CurrentStepProps {
  className?: string
}

const stepsComponents = {
  [TradeStepType.GasStation]: GasStationStep,
  [TradeStepType.StartTrade]: StartTradeStep,
  [TradeStepType.Trade]: TradeStep,
  [TradeStepType.Summary]: SummaryStep,
  [TradeStepType.Success]: SuccessStep,
  [TradeStepType.Recovery]: RecoveryStep,
  [TradeStepType.RecoverySuccess]: RecoverySuccessStep,
}

const CurrentStep = ({ className }: CurrentStepProps) => {
  const route = useAtomValue(routeAtom)
  const isComplete = useAtomValue(isCompleteAtom)
  const [, setMode] = useAtom(viewModeAtom)

  const stepsNeeded = useAtomValue(stepsNeededAtom)
  const activeSteps = useAtomValue(activeStepsAtom)

  const currentStepIndex = useMemo(() => {
    let index = 0
    for (const [key, isActive] of activeSteps)
      if (isActive) {
        index = stepsNeeded.indexOf(key)
        break
      }
    return index
  }, [activeSteps, stepsNeeded])

  return (
    <button
      type='button'
      className={cn(
        'px-5 overflow-hidden group relative w-full rounded-t-3xl cursor-pointer transition-colors hover:bg-surface2 focus-inner',
        {
          'cursor-auto hover:bg-transparent': isComplete,
        },
        className,
      )}
      onClick={() => {
        if (!isComplete) {
          setMode('overview')
        }
      }}
      style={{
        // @ts-ignore
        '--current-step-index': currentStepIndex,
      }}
      key={`${route?.ID}-step-container`}
    >
      {!isComplete && (
        <div className='absolute flex items-center gap-2 top-4 right-4 translate-x-[70px] group-hover:translate-x-0 transition-all ease-in-out duration-300'>
          <Maximize2
            className='text-tertiary-100 group-hover:text-primary transition-all ease-in-out duration-300'
            strokeWidth={2.5}
            size={16}
          />

          <p className='text-sm text-primary opacity-0 group-hover:opacity-100 transition-all w-[70px]'>
            View route
          </p>
        </div>
      )}
      <ul
        className={cn(
          'relative flex flex-col transition-transform duration-500 ease-in-out',
          {
            'h-[192px]': !isComplete,
            'h-[372px]': isComplete,
          },
        )}
        style={{
          transform: 'translateY(calc(-100% * var(--current-step-index)))',
        }}
      >
        {stepsNeeded.map((key) => {
          const Component = stepsComponents[key]
          return <Component key={key} />
        })}
      </ul>
    </button>
  )
}
